package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.data.colaboradores.ColabEscopo
import br.com.htms.inspeccaocampo.data.colaboradores.ColabEscopoTag
import br.com.htms.inspeccaocampo.platform.randomUuid

/** Função em campo (TAG) atribuída aos usuários. */
data class TagConfig(
    val id: String,
    val rotulo: String
) {
    companion object {
        val SISTEMA = setOf("TST", "ADM", "SUPERVISOR", "ENGENHEIRO")
        private val idRegex = Regex("^[A-Z][A-Z0-9_]{0,20}$")

        fun padrao(): List<TagConfig> = listOf(
            TagConfig("TST", "TST"),
            TagConfig("ADM", "ADM"),
            TagConfig("SUPERVISOR", "Supervisor"),
            TagConfig("ENGENHEIRO", "Engenheiro")
        )

        fun normalizarId(raw: String): String =
            raw.trim().uppercase()
                .replace(Regex("[^A-Z0-9]+"), "_")
                .trim('_')
                .take(21)

        fun idValido(raw: String): Boolean = idRegex.matches(raw)
    }
}

data class CampoConfig(
    val id: String = "campo_${randomUuid()}",
    val titulo: String,
    val ativo: Boolean = true,
    val obrigatorio: Boolean = true,
    val fotoObrigatoriaSeSim: Boolean = true,
    val fotoObrigatoriaSeNao: Boolean = true
)

/** Destino do e-mail de aviso, independente de quem acessa o formulário. */
enum class FormEmailDestino(val id: String, val rotulo: String) {
    AUTO("AUTO", "Automático (pelo acesso)"),
    TST("TST", "E-mail TST"),
    ADM("ADM", "E-mail ADM"),
    AMBOS("AMBOS", "TST + ADM");

    companion object {
        fun fromId(raw: String?): FormEmailDestino =
            entries.find { it.id.equals(raw, ignoreCase = true) } ?: AUTO
    }
}

/** Quem pode ver/preencher cada formulário do hub (TST, ADM, Supervisor e/ou Engenheiro). */
data class FormAcessoConfig(
    val formId: String,
    val tst: Boolean = true,
    val adm: Boolean = false,
    val supervisor: Boolean = false,
    val engenheiro: Boolean = false,
    /** Para quem vai o e-mail ao salvar (pode divergir do acesso). */
    val emailDestino: FormEmailDestino = FormEmailDestino.AUTO,
    /** Outros destinatários deste formulário, separados por vírgula. */
    val emailsExtras: String = ""
) {
    val algumPerfil: Boolean get() = tst || adm || supervisor || engenheiro

    companion object {
        fun padraoPara(tipo: FormTipo): FormAcessoConfig = when (tipo) {
            // Alinhado ao hub Config: TST vs ADM por formulário.
            FormTipo.INSPECAO_CAMPO,
            FormTipo.RELATORIO_ALOJAMENTO,
            FormTipo.RELATORIO_FOTO_ALOJAMENTO,
            FormTipo.CHECKLIST_ALOJAMENTO,
            FormTipo.CHECKLIST_VETORES,
            FormTipo.COMUNICACAO_OCORRENCIAS ->
                FormAcessoConfig(formId = tipo.id, tst = true, adm = false)
            FormTipo.CHECKLIST_DIARIO_HTMS,
            FormTipo.CHECKLIST_DIARIO_AXIA ->
                FormAcessoConfig(formId = tipo.id, tst = false, adm = true)
            FormTipo.REM_SEGURANCA_OBRAS,
            FormTipo.RELATORIO_SEGURANCA,
            FormTipo.ACOMPANHAMENTO_AMBIENTAL,
            FormTipo.EVIDENCIA_TREINAMENTO,
            FormTipo.ANALISE_VISUAL_NR ->
                FormAcessoConfig(formId = tipo.id, tst = true, adm = true)
        }

        fun listaPadraoHub(): List<FormAcessoConfig> =
            FormTipo.hubEntries.map { padraoPara(it) }

        fun completarLista(atuais: List<FormAcessoConfig>): List<FormAcessoConfig> {
            val byId = atuais.associateBy { it.formId.uppercase() }
            return FormTipo.hubEntries.map { tipo ->
                val found = byId[tipo.id.uppercase()]
                when {
                    found == null -> padraoPara(tipo)
                    !found.algumPerfil -> padraoPara(tipo).copy(emailDestino = found.emailDestino)
                    else -> found.copy(formId = tipo.id)
                }
            }
        }
    }
}

data class AppConfig(
    val nomeApp: String = "HTMS ENERGIA",
    val nomeInspecao: String = "Portal QSSMA",
    val bannerPath: String? = null,
    /** Campos da Inspeção de Campo Diária. */
    val campos: List<CampoConfig> = camposPadrao(),
    /** Itens da Parte 2 (Checklist) do Relatório Alojamento. */
    val camposAlojamentoChecklist: List<CampoConfig> = camposAlojamentoChecklistPadrao(),
    /** Itens da Parte 1 (Relatório fotográfico) do Relatório Alojamento. */
    val camposAlojamentoFoto: List<CampoConfig> = camposAlojamentoFotoPadrao(),
    /** Campos da Comunicação Prévia de ocorrências (visibilidade / rótulo). */
    val camposOcorrencias: List<CampoConfig> = camposOcorrenciasPadrao(),
    /**
     * E-mail do aviso por formulário. Os campos de TAG (tst/adm/...) são a regra antiga de acesso,
     * usada só por sessões sem `acessos`; o acesso agora fica no cadastro de cada usuário.
     */
    val formAcessos: List<FormAcessoConfig> = FormAcessoConfig.listaPadraoHub(),
    /** Destino de e-mail para formulários marcados como TST. */
    val emailDestinoTst: String = "admqsms@htmsgroup.com",
    /** Destino de e-mail para formulários marcados como ADM. */
    val emailDestinoAdm: String = "logistica@htmsgroup.com",
    /** Regra antiga (sessões sem `acessos`): admins liberados para Emissão de Documentos. */
    val emissaoDocUsuarios: List<String> = emptyList(),
    /** Regra antiga (sessões sem `acessos`): usuários liberados para o Controle de Ativos. */
    val ativosUsuarios: List<String> = emptyList(),
    /** TAGs que aparecem no cadastro de usuários. */
    val tags: List<TagConfig> = TagConfig.padrao(),
    /** Teto de abas e documentos de Colaboradores QSMS por TAG. Vazio = padrão (DP já nasce limitada). */
    val colabEscopos: List<ColabEscopoTag> = emptyList()
) {
    fun tagsEfetivas(): List<TagConfig> = tags.ifEmpty { TagConfig.padrao() }

    fun tagsSanitizadas(): List<TagConfig> {
        val out = mutableListOf<TagConfig>()
        for (tag in tagsEfetivas()) {
            val id = if (tag.id in TagConfig.SISTEMA) tag.id else TagConfig.normalizarId(tag.id)
            if (!TagConfig.idValido(id) || out.any { it.id == id }) continue
            out.add(TagConfig(id, tag.rotulo.trim().take(40).ifBlank { id }))
        }
        return out.ifEmpty { TagConfig.padrao() }
    }

    fun atualizarTag(idAtual: String, novoId: String, rotulo: String): AppConfig {
        val atual = tagsEfetivas()
        val sistema = idAtual in TagConfig.SISTEMA
        val id = if (sistema) idAtual else TagConfig.normalizarId(novoId)
        if (!TagConfig.idValido(id)) return this
        if (atual.any { it.id == id && it.id != idAtual }) return this
        return copy(
            tags = atual.map {
                if (it.id == idAtual) TagConfig(id, rotulo.take(40)) else it
            },
            colabEscopos = colabEscopos.map { if (it.tagId == idAtual) it.copy(tagId = id) else it }
        )
    }

    fun excluirTag(id: String): AppConfig {
        val atual = tagsEfetivas()
        if (atual.size <= 1) return this
        return copy(
            tags = atual.filterNot { it.id == id },
            colabEscopos = colabEscopos.filterNot { it.tagId == id }
        )
    }

    fun definirColabEscopo(escopo: ColabEscopoTag): AppConfig {
        val limpo = ColabEscopo.sanitizar(escopo)
        if (!TagConfig.idValido(limpo.tagId)) return this
        val sem = colabEscopos.filterNot { it.tagId == limpo.tagId }
        val gravar = limpo.limitado || limpo.tagId == "DP"
        return copy(colabEscopos = if (gravar) sem + limpo else sem)
    }

    fun adicionarTag(idRaw: String, rotulo: String): AppConfig {
        val atual = tagsEfetivas()
        if (atual.size >= 24) return this
        val id = TagConfig.normalizarId(idRaw).ifBlank { TagConfig.normalizarId(rotulo) }
        if (!TagConfig.idValido(id) || atual.any { it.id == id }) return this
        return copy(tags = atual + TagConfig(id, rotulo.trim().take(40).ifBlank { id }))
    }

    fun rotuloTag(id: String): String =
        tagsEfetivas().find { it.id.equals(id, ignoreCase = true) }?.rotulo?.trim()?.ifBlank { null }
            ?: id

    fun ativosLiberado(username: String): Boolean =
        ativosUsuarios.any { it.equals(username.trim(), ignoreCase = true) }

    fun emissaoDocLiberada(username: String): Boolean =
        emissaoDocUsuarios.any { it.equals(username.trim(), ignoreCase = true) }

    fun camposAtivos(): List<CampoConfig> = campos.filter { it.ativo }

    fun camposAlojamentoChecklistAtivos(): List<CampoConfig> =
        camposAlojamentoChecklist.filter { it.ativo }

    fun camposAlojamentoFotoAtivos(): List<CampoConfig> =
        camposAlojamentoFoto.filter { it.ativo }

    fun campoOcorrencia(id: String): CampoConfig? =
        camposOcorrencias.find { it.id == id }

    fun ocorrenciaAtiva(id: String): Boolean =
        campoOcorrencia(id)?.ativo != false

    fun rotuloOcorrencia(id: String, padrao: String): String =
        campoOcorrencia(id)?.titulo?.takeIf { it.isNotBlank() } ?: padrao

    fun acessoDoForm(tipo: FormTipo): FormAcessoConfig {
        val found = formAcessos.find { it.formId.equals(tipo.id, ignoreCase = true) }
            ?: return FormAcessoConfig.padraoPara(tipo)
        // Entrada inválida (todos os perfis desligados) → volta ao padrão do formulário,
        // mas preserva o seletor de e-mail.
        if (!found.algumPerfil) {
            return FormAcessoConfig.padraoPara(tipo).copy(emailDestino = found.emailDestino)
        }
        return found
    }

    /** Ignora a mudança se ela deixaria o formulário sem nenhum perfil. */
    fun comAcessoForm(
        tipo: FormTipo,
        tst: Boolean,
        adm: Boolean,
        supervisor: Boolean = acessoDoForm(tipo).supervisor,
        engenheiro: Boolean = acessoDoForm(tipo).engenheiro,
        emailDestino: FormEmailDestino? = null
    ): AppConfig {
        val atualAcesso = acessoDoForm(tipo)
        val atual = FormAcessoConfig(
            formId = tipo.id,
            tst = tst,
            adm = adm,
            supervisor = supervisor,
            engenheiro = engenheiro,
            emailDestino = emailDestino ?: atualAcesso.emailDestino,
            emailsExtras = atualAcesso.emailsExtras
        )
        if (!atual.algumPerfil) return this
        val resto = formAcessos.filterNot { it.formId.equals(tipo.id, ignoreCase = true) }
        return copy(formAcessos = FormAcessoConfig.completarLista(resto + atual))
    }

    fun comEmailForm(tipo: FormTipo, emailDestino: FormEmailDestino): AppConfig {
        val atual = acessoDoForm(tipo)
        return comAcessoForm(
            tipo,
            tst = atual.tst,
            adm = atual.adm,
            supervisor = atual.supervisor,
            engenheiro = atual.engenheiro,
            emailDestino = emailDestino
        )
    }

    fun comEmailsExtras(tipo: FormTipo, emailsExtras: String): AppConfig {
        val atual = acessoDoForm(tipo)
        val resto = formAcessos.filterNot { it.formId.equals(tipo.id, ignoreCase = true) }
        return copy(
            formAcessos = FormAcessoConfig.completarLista(
                resto + atual.copy(formId = tipo.id, emailsExtras = emailsExtras)
            )
        )
    }

    /** Garante que todos os formulários do hub tenham entrada de acesso. */
    fun comFormAcessosCompletos(): AppConfig =
        copy(formAcessos = FormAcessoConfig.completarLista(formAcessos))

    /** JSON remoto não carrega banner local do aparelho. */
    fun semBannerLocal(): AppConfig = copy(bannerPath = null)

    /**
     * Mescla config do servidor com o banner local deste aparelho.
     */
    fun mesclarComBannerLocal(localBannerPath: String?): AppConfig =
        copy(bannerPath = localBannerPath)

    /**
     * E-mails de aviso:
     * - se emailDestino = AUTO → segue switches TST/ADM do formulário
     * - TST / ADM / AMBOS → força o(s) destino(s), independente do acesso
     */
    fun emailsParaForm(tipo: FormTipo): List<String> {
        val acesso = acessoDoForm(tipo)
        val querTst: Boolean
        val querAdm: Boolean
        when (acesso.emailDestino) {
            FormEmailDestino.AUTO -> {
                querTst = acesso.tst
                querAdm = acesso.adm
            }
            FormEmailDestino.TST -> {
                querTst = true
                querAdm = false
            }
            FormEmailDestino.ADM -> {
                querTst = false
                querAdm = true
            }
            FormEmailDestino.AMBOS -> {
                querTst = true
                querAdm = true
            }
        }
        val out = linkedSetOf<String>()
        if (querTst) acrescentarEmails(out, emailDestinoTst)
        if (querAdm) acrescentarEmails(out, emailDestinoAdm)
        if (out.isEmpty()) acrescentarEmails(out, emailDestinoTst)
        acrescentarEmails(out, acesso.emailsExtras)
        return out.toList()
    }

    private fun acrescentarEmails(destino: LinkedHashSet<String>, raw: String) {
        val vistos = destino.map { it.lowercase() }.toMutableSet()
        for (parte in raw.split(',', ';', '\n')) {
            val email = parte.trim()
            if (!email.contains('@')) continue
            if (vistos.add(email.lowercase())) destino.add(email)
        }
    }

    companion object {
        fun camposPadrao(): List<CampoConfig> = listOf(
            CampoConfig(id = "item_1", titulo = "APR assinada?", fotoObrigatoriaSeNao = false),
            CampoConfig(id = "item_2", titulo = "PT emitida (quando aplicável)?", fotoObrigatoriaSeNao = false),
            CampoConfig(id = "item_3", titulo = "EPIs adequados para executar a atividade?"),
            CampoConfig(id = "item_4", titulo = "Ferramentas inspecionadas?", fotoObrigatoriaSeNao = false),
            CampoConfig(id = "item_5", titulo = "Equipamentos em conformidade?"),
            CampoConfig(id = "item_6", titulo = "Área sinalizada e isolada?"),
            CampoConfig(id = "item_7", titulo = "Área organizada e limpa?"),
            CampoConfig(id = "item_8", titulo = "Aterramento instalado e em conformidade?")
        )

        fun camposAlojamentoChecklistPadrao(): List<CampoConfig> =
            ChecklistAlojamentoCatalog.itensBase().map { item ->
                CampoConfig(
                    id = item.id,
                    titulo = item.titulo,
                    ativo = true,
                    obrigatorio = item.obrigatorio,
                    fotoObrigatoriaSeSim = item.fotoObrigatoria,
                    fotoObrigatoriaSeNao = false
                )
            }

        fun camposAlojamentoFotoPadrao(): List<CampoConfig> =
            RelatorioFotoCatalog.itensBase().map { item ->
                CampoConfig(
                    id = item.id,
                    titulo = item.titulo,
                    ativo = true,
                    obrigatorio = item.obrigatorio,
                    fotoObrigatoriaSeSim = item.fotoObrigatoria,
                    fotoObrigatoriaSeNao = false
                )
            }

        fun camposOcorrenciasPadrao(): List<CampoConfig> = listOf(
            CampoConfig(id = "cliente", titulo = "Cliente", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "obra", titulo = "Nome da obra", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "caracterizacao", titulo = "Caracterização", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "tipo", titulo = "Tipo", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "dataHoraOcorrido", titulo = "Data/hora do ocorrido", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "localExato", titulo = "Local exato", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "quemReportando", titulo = "Quem está reportando", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "tipoEvento", titulo = "Classe do evento", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "envolvido", titulo = "Envolvido (nome/função)", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "responsavel", titulo = "Responsável pela atividade", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "dadosAcidente", titulo = "Dados do acidente", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "acoesTomadasImediato", titulo = "Ações tomadas no imediato", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "descricaoEvento", titulo = "Descrição do evento", fotoObrigatoriaSeSim = false, fotoObrigatoriaSeNao = false),
            CampoConfig(id = "fotos", titulo = "Fotos da ocorrência", fotoObrigatoriaSeSim = true, fotoObrigatoriaSeNao = false)
        )

        fun padrao(): AppConfig = AppConfig()
    }
}
